import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldX,
  Smartphone, Monitor, Tablet, Globe,
  Clock, MapPin, LogOut, Key, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, Eye, EyeOff,
  Chrome, Wifi
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant Badge de sécurité
const SecurityBadge = ({ status, score }) => {
  const configs = {
    secure: {
      icon: ShieldCheck,
      color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      iconColor: 'text-emerald-600',
      label: 'Sécurisé'
    },
    moderate: {
      icon: Shield,
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      iconColor: 'text-amber-600',
      label: 'Modéré'
    },
    warning: {
      icon: ShieldAlert,
      color: 'bg-red-100 text-red-700 border-red-200',
      iconColor: 'text-red-600',
      label: 'Attention'
    }
  };
  
  const config = configs[status] || configs.moderate;
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${config.color}`}>
      <Icon className={`w-5 h-5 ${config.iconColor}`} />
      <span className="font-semibold">{config.label}</span>
      <span className="text-sm opacity-75">({score}/100)</span>
    </div>
  );
};

// Composant pour afficher un device
const DeviceIcon = ({ type }) => {
  const icons = {
    mobile: Smartphone,
    tablet: Tablet,
    desktop: Monitor
  };
  const Icon = icons[type] || Monitor;
  return <Icon className="w-5 h-5" />;
};

// Composant Session
const SessionCard = ({ session, onRevoke, isLoading, isCurrent }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Inconnue';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`flex items-center justify-between p-4 rounded-xl border ${
      isCurrent ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'
    } hover:shadow-sm transition-shadow`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          isCurrent ? 'bg-blue-100' : 'bg-slate-100'
        }`}>
          <DeviceIcon type={session.device_type} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900">
              {session.browser} sur {session.os}
            </span>
            {isCurrent && (
              <Badge className="bg-blue-600 text-white text-xs">Session actuelle</Badge>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {session.ip_address}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {formatDate(session.last_activity)}
            </span>
          </div>
        </div>
      </div>
      
      {!isCurrent && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRevoke(session.session_id)}
          disabled={isLoading}
          className="text-red-600 border-red-200 hover:bg-red-50"
          data-testid={`revoke-session-${session.session_id}`}
        >
          <LogOut className="w-4 h-4 mr-1" />
          Révoquer
        </Button>
      )}
    </div>
  );
};

// Composant Historique de connexion
const LoginHistoryItem = ({ login }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Inconnue';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${
      login.suspicious ? 'bg-red-50 border border-red-200' : 'bg-slate-50'
    }`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          login.suspicious ? 'bg-red-100' : 'bg-slate-200'
        }`}>
          <DeviceIcon type={login.device_type} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 text-sm">
              {login.browser} • {login.os}
            </span>
            {login.suspicious && (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Suspect
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span>{login.ip_address}</span>
            <span>{formatDate(login.timestamp)}</span>
          </div>
        </div>
      </div>
      
      {login.success ? (
        <CheckCircle className="w-5 h-5 text-emerald-500" />
      ) : (
        <XCircle className="w-5 h-5 text-red-500" />
      )}
    </div>
  );
};

// Page principale
const SecurityPage = () => {
  const { token, user } = useAuth();
  const [securityStatus, setSecurityStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const fetchSecurityData = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [statusRes, sessionsRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/auth/security-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/auth/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/auth/login-history`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setSecurityStatus(statusData);
      }
      
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setSessions(sessionsData.sessions || []);
      }
      
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setLoginHistory(historyData.login_history || []);
      }
      
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Erreur lors du chargement des données de sécurité');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSecurityData();
  }, [fetchSecurityData]);

  const handleRevokeSession = async (sessionId) => {
    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/api/auth/sessions/revoke/${sessionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Session révoquée avec succès');
        fetchSecurityData();
      } else {
        toast.error('Erreur lors de la révocation');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir déconnecter tous les autres appareils ?')) {
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await fetch(`${API_URL}/api/auth/sessions/revoke-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.revoked_count} session(s) révoquée(s)`);
        fetchSecurityData();
      } else {
        toast.error('Erreur lors de la révocation');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-slate-200 rounded-xl" />
          <div className="h-64 bg-slate-200 rounded-xl" />
          <div className="h-48 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  const displayedHistory = showAllHistory ? loginHistory : loginHistory.slice(0, 5);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="security-page">
      {/* Header avec badge de sécurité */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sécurité du compte</h1>
          <p className="text-slate-600 mt-1">Gérez la sécurité et les accès à votre compte</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSecurityData}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Statut de sécurité */}
      {securityStatus && (
        <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <SecurityBadge status={securityStatus.status} score={securityStatus.score} />
                </div>
                <p className="text-slate-300 text-lg">{securityStatus.message}</p>
                
                <div className="grid grid-cols-3 gap-6 mt-6">
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                    <div className="flex items-center gap-2 mb-2">
                      <Key className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-slate-300">2FA</span>
                    </div>
                    <span className={`font-bold ${
                      securityStatus.details.two_factor_enabled ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {securityStatus.details.two_factor_enabled ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                  
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                    <div className="flex items-center gap-2 mb-2">
                      <Wifi className="w-5 h-5 text-cyan-400" />
                      <span className="text-sm text-slate-300">Sessions</span>
                    </div>
                    <span className="font-bold text-white">
                      {securityStatus.details.active_sessions_count} active(s)
                    </span>
                  </div>
                  
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span className="text-sm text-slate-300">Alertes</span>
                    </div>
                    <span className={`font-bold ${
                      securityStatus.details.suspicious_logins_count === 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {securityStatus.details.suspicious_logins_count} suspecte(s)
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <Shield className="w-32 h-32 text-white/10" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 2FA */}
      <Card data-testid="2fa-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-blue-600" />
                Authentification à deux facteurs (2FA)
              </CardTitle>
              <CardDescription>
                Ajoutez une couche de sécurité supplémentaire à votre compte
              </CardDescription>
            </div>
            <Badge className={
              user?.two_factor_enabled 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-slate-100 text-slate-700'
            }>
              {user?.two_factor_enabled ? 'Activé' : 'Désactivé'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                user?.two_factor_enabled ? 'bg-emerald-100' : 'bg-slate-200'
              }`}>
                {user?.two_factor_enabled ? (
                  <ShieldCheck className="w-6 h-6 text-emerald-600" />
                ) : (
                  <ShieldX className="w-6 h-6 text-slate-500" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-900">
                  {user?.two_factor_enabled 
                    ? 'Votre compte est protégé par la 2FA'
                    : 'Protégez votre compte avec la 2FA'}
                </p>
                <p className="text-sm text-slate-500">
                  {user?.two_factor_enabled 
                    ? 'Un code est requis à chaque connexion'
                    : 'Recevez un code par email à chaque connexion'}
                </p>
              </div>
            </div>
            <Button
              variant={user?.two_factor_enabled ? 'outline' : 'default'}
              className={!user?.two_factor_enabled ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid="configure-2fa-btn"
            >
              {user?.two_factor_enabled ? 'Gérer' : 'Activer'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sessions actives */}
      <Card data-testid="sessions-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-600" />
                Sessions actives
              </CardTitle>
              <CardDescription>
                Appareils actuellement connectés à votre compte
              </CardDescription>
            </div>
            {sessions.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevokeAllSessions}
                disabled={actionLoading}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="revoke-all-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnecter tout
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Monitor className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune session active trouvée</p>
            </div>
          ) : (
            sessions.map((session, index) => (
              <SessionCard
                key={session.session_id}
                session={session}
                onRevoke={handleRevokeSession}
                isLoading={actionLoading}
                isCurrent={index === 0}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Historique des connexions */}
      <Card data-testid="history-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Historique des connexions
              </CardTitle>
              <CardDescription>
                Dernières activités de connexion sur votre compte
              </CardDescription>
            </div>
            <Badge variant="outline">{loginHistory.length} connexion(s)</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {loginHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun historique de connexion</p>
            </div>
          ) : (
            <>
              {displayedHistory.map((login, index) => (
                <LoginHistoryItem key={index} login={login} />
              ))}
              
              {loginHistory.length > 5 && (
                <Button
                  variant="ghost"
                  className="w-full mt-2"
                  onClick={() => setShowAllHistory(!showAllHistory)}
                  data-testid="toggle-history-btn"
                >
                  {showAllHistory ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      Voir moins
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Voir tout ({loginHistory.length - 5} de plus)
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Conseils de sécurité */}
      <Card className="bg-blue-50 border-blue-200" data-testid="tips-section">
        <CardContent className="p-6">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Conseils de sécurité
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
              Activez l'authentification à deux facteurs (2FA) pour une protection maximale
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
              Révoquez les sessions que vous ne reconnaissez pas immédiatement
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
              Utilisez un mot de passe unique et complexe pour votre compte
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
              Vérifiez régulièrement l'historique de vos connexions
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityPage;
